import isValid from 'date-fns/isValid';
import isMatch from 'date-fns/isMatch';
import format from 'date-fns/format';
import differenceInMonths from 'date-fns/differenceInMonths';
import parse from 'date-fns/parse';
import { NetworkRequest } from '../Helpers/NetworkRequest';

// eslint-disable-next-line
const nameRegExp = /^[A-Za-z’ \.-]{0,20}$/;
const emailRegExp = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w+)+$/;
const phoneRegExp = /^[+]?[(]?[2-9]{1}[0-9]{2}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
const addressRegExp = /\d+(?:\s+\S+)+/;
const zipCodeRegExp = /(^\d{5}$)/;

export const dateIsValidAndMatchesFormat = (date, dateFormat = 'MM-dd-yyyy') => {
  let valid = !!date;
  if (valid && date.indexOf('-') > -1) {
    date.split('-').forEach((num) => {
      if (!num || Number.isNaN(+num) || num.length > 4) {
        valid = false;
      }
    });
  }
  const parsedDate = parse(date, dateFormat, new Date());
  return valid && isValid((new Date(parsedDate))) && isMatch(date, dateFormat);
};

export const isAgeOverEightTeenLessThanHundred = (valid, value, dateFormat = 'MM-dd-yyyy') => {
  if (valid) {
    const currentDate = new Date();
    const parsedDate = parse(value, dateFormat, new Date());
    const difference = differenceInMonths(currentDate, (new Date(parsedDate)));
    const eighteenYearsInMonths = 12 * 18;
    const isOver18 = difference >= eighteenYearsInMonths;
    const oneHundredYearsInMonths = 12 * 100;
    const isUnder100 = difference < oneHundredYearsInMonths;
    if (!isOver18 || !isUnder100) {
      return false;
    }
  }
  return true;
};

export default class GenericValidation {
  static formatValidationReturn(valid, errorMessage) {
    let message = '';
    if (!valid) {
      message = errorMessage;
    }
    return { valid, message };
  }

  static validateStringExists(value) {
    return !!value && typeof value === 'string' && value.length > 0;
  }

  static validateValueIsLessThan45(value) {
    return !!value && value.length < 45;
  }

  static validateSelectOption(value, defaultVal) {
    return value !== defaultVal;
  }

  static validateNoEmptyValue(value, defaultVal = '') {
    const result = (value === false || (!!value && value !== defaultVal));
    return GenericValidation.formatValidationReturn(result, 'Please enter a valid value.');
  }

  static validateNoEmptyValueAllowFalse(value, defaultVal = '') {
    const result = (value === false || (value !== defaultVal));
    return GenericValidation.formatValidationReturn(result, 'Please enter a valid value.');
  }

  static validateName(value) {
    return GenericValidation.validateStringExists(value) && nameRegExp.test(value);
  }

  static validateFirstAndLastName(firstName, lastName) {
    const validFirstName = GenericValidation.validateStringExists(firstName)
      && nameRegExp.test(firstName);
    const validLastName = GenericValidation.validateStringExists(lastName)
      && nameRegExp.test(lastName);
    const message = 'Please enter a valid name';
    return {
      firstNameValidation: GenericValidation.formatValidationReturn(validFirstName, message),
      lastNameValidation: GenericValidation.formatValidationReturn(validLastName, message),
    };
  }

  static validateEmail(value) {
    return GenericValidation.validateStringExists(value) && emailRegExp.test(value) && !/\.gov$/.test(value);
  }

  static validateBasicEmail(value) {
    const validEmail = GenericValidation.validateEmail(value);
    const message = 'Please enter a valid email';
    return GenericValidation.formatValidationReturn(validEmail, message);
  }

  static async validateAddress(value) {
    const valid = GenericValidation.validateStringExists(value)
      && addressRegExp.test(value)
      && value.toString().length >= 5
      && GenericValidation.validateValueIsLessThan45(value);
    const message = 'Please enter a valid address';
    return GenericValidation.formatValidationReturn(valid, message);
  }

  static async promptAddressValidation(params) {
    try {
      if (localStorage.getItem('promptedOnce') === 'true') {
        return { success: true, block: false, message: '' };
      }
      const result = await NetworkRequest('validateAddress', params);
      const { data } = result;
      if (data && data.success === false && data.block === false) {
        localStorage.setItem('promptedOnce', 'true');
      }
      return data;
    } catch (e) {
      console.error(e);
      return { success: true, block: false, message: '' };
    }
  }

  static async validateAddressTesting(value) {
    return GenericValidation.validateStringExists(value)
      && addressRegExp.test(value)
      && GenericValidation.validateValueIsLessThan45(value);
  }

  static validatePhone(value) {
    return phoneRegExp.test(value);
  }

  static validateZipCode(value) {
    return zipCodeRegExp.test(value);
  }

  static validateYear(value) {
    const parsedDate = parse(`${value}-01-01`, 'yyyy-MM-dd', new Date());
    return !!value && value.toString().length === 4
      && isValid((new Date(parsedDate)))
      && +value <= +format(new Date(parsedDate), 'yyyy') + 1;
  }

  static handleErrorData(validationsArray, validValue, data) {
    let valid = validValue;
    const [errorStateNames, errorValues, errorMessages] = [[], [], []];
    validationsArray.forEach(({ name, validation }) => {
      if (!validation.valid) {
        valid = false;
        errorStateNames.push(name);
        errorValues.push(data[name].value);
        errorMessages.push(validation.message);
      }
    });
    return [errorStateNames, errorValues, errorMessages, valid];
  }
}
